import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Badge, Nav, Tab, Row, Col, Table } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { apiClient, API_HOST_BASE } from '../utils/apiUtils';
import { useNotification } from '../hooks/useNotification';
import './ReportModule.css';

const EvaluationReportModal = ({ show, onHide, reportId, onSuccess }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('project');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { notification, showSuccess, showError, dismiss } = useNotification();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [wasNewReport, setWasNewReport] = useState(false);
  
  // Form state
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [participantData, setParticipantData] = useState({
    participant_type: '',
    male_batstateu_participants: '',
    female_batstateu_participants: '',
    male_other_participants: '',
    female_other_participants: '',
    narrative_of_activity: '',
    // Activity rating fields
    activity_excellent_batstateu: '',
    activity_excellent_other: '',
    activity_very_satisfactory_batstateu: '',
    activity_very_satisfactory_other: '',
    activity_satisfactory_batstateu: '',
    activity_satisfactory_other: '',
    activity_fair_batstateu: '',
    activity_fair_other: '',
    activity_poor_batstateu: '',
    activity_poor_other: '',
    // Timeliness rating fields
    timeliness_excellent_batstateu: '',
    timeliness_excellent_other: '',
    timeliness_very_satisfactory_batstateu: '',
    timeliness_very_satisfactory_other: '',
    timeliness_satisfactory_batstateu: '',
    timeliness_satisfactory_other: '',
    timeliness_fair_batstateu: '',
    timeliness_fair_other: '',
    timeliness_poor_batstateu: '',
    timeliness_poor_other: ''
  });
  const [existingReport, setExistingReport] = useState(null);
  const [reportStatus, setReportStatus] = useState('');
  const [photoRows, setPhotoRows] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [preparedBySignature, setPreparedBySignature] = useState(null); // Signature file for "Prepared by"
  const [signaturePreview, setSignaturePreview] = useState(null); // Preview URL for signature
  const [trackingNumber, setTrackingNumber] = useState('');
  const [signatoryPrefs, setSignatoryPrefs] = useState({
    prepared_by_enabled: true,
    reviewed_by_enabled: true,
    accepted_by_enabled: true
  });

  const isEditMode = !!reportId;

  // Check permissions
  const canViewReports = user && ['Extension Coordinator','Extension Head','GAD','Vice Chancellor for Administration and Finance','Vice Chancellor for Research, Development and Extension Services','Chancellor','Admin'].includes(user.role);

  useEffect(() => {
    if (show && canViewReports) {
      fetchProjects();
      if (isEditMode) {
        fetchExistingReport();
      } else {
        resetForm();
        // Initialize with 3 default photo rows for new reports
        const defaultRows = Array.from({ length: 3 }, (_, i) => ({
          row_id: `row_${Date.now()}_${i}`,
          frameCount: 1,
          photos: [],
          caption: '',
          captionType: 'row',
          photoCaptions: [],
          selectedFiles: [],
          tempFiles: []
        }));
        setPhotoRows(defaultRows);
      }
    }
  }, [show, isEditMode, reportId, canViewReports]);

  const resetForm = () => {
    setSelectedProject(null);
    setExistingReport(null);
    setPhotos([]);
    setPreparedBySignature(null);
    setSignaturePreview(null);
    // Initialize with 3 default photo rows
    const defaultRows = Array.from({ length: 3 }, (_, i) => ({
      row_id: `row_${Date.now()}_${i}`,
      frameCount: 1,
      photos: [],
      caption: '',
      captionType: 'row',
      photoCaptions: [],
      selectedFiles: [],
      tempFiles: []
    }));
    setPhotoRows(defaultRows);
    dismiss();
    setTrackingNumber('');
    setSignatoryPrefs({
      prepared_by_enabled: true,
      reviewed_by_enabled: true,
      accepted_by_enabled: true
    });
    setParticipantData({
      participant_type: '',
      male_batstateu_participants: '',
      female_batstateu_participants: '',
      male_other_participants: '',
      female_other_participants: '',
      narrative_of_activity: '',
      activity_excellent_batstateu: '',
      activity_excellent_other: '',
      activity_very_satisfactory_batstateu: '',
      activity_very_satisfactory_other: '',
      activity_satisfactory_batstateu: '',
      activity_satisfactory_other: '',
      activity_fair_batstateu: '',
      activity_fair_other: '',
      activity_poor_batstateu: '',
      activity_poor_other: '',
      timeliness_excellent_batstateu: '',
      timeliness_excellent_other: '',
      timeliness_very_satisfactory_batstateu: '',
      timeliness_very_satisfactory_other: '',
      timeliness_satisfactory_batstateu: '',
      timeliness_satisfactory_other: '',
      timeliness_fair_batstateu: '',
      timeliness_fair_other: '',
      timeliness_poor_batstateu: '',
      timeliness_poor_other: ''
    });
    setActiveTab('project');
  };

  const toggleSignatoryPref = (key) => {
    setSignatoryPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get('/reports/projects');
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects', err);
      showError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingReport = async () => {
    try {
      setLoading(true);
      const { data: report } = await apiClient.get(`/reports/${reportId}`);
      setExistingReport(report);
      
      const { data: project } = await apiClient.get(`/projects/${report.project_id}`);
      setSelectedProject(project);
      
      setTrackingNumber(report.tracking_number || '');
      setSignatoryPrefs({
        prepared_by_enabled: report.prepared_by_enabled !== undefined ? !!report.prepared_by_enabled : true,
        reviewed_by_enabled: report.reviewed_by_enabled !== undefined ? !!report.reviewed_by_enabled : true,
        accepted_by_enabled: report.accepted_by_enabled !== undefined ? !!report.accepted_by_enabled : true
      });
      setParticipantData({
        participant_type: report.participant_type || '',
        male_batstateu_participants: report.male_batstateu_participants || '',
        female_batstateu_participants: report.female_batstateu_participants || '',
        male_other_participants: report.male_other_participants || '',
        female_other_participants: report.female_other_participants || '',
        narrative_of_activity: report.narrative_of_activity || '',
        activity_excellent_batstateu: report.activity_excellent_batstateu || '',
        activity_excellent_other: report.activity_excellent_other || '',
        activity_very_satisfactory_batstateu: report.activity_very_satisfactory_batstateu || '',
        activity_very_satisfactory_other: report.activity_very_satisfactory_other || '',
        activity_satisfactory_batstateu: report.activity_satisfactory_batstateu || '',
        activity_satisfactory_other: report.activity_satisfactory_other || '',
        activity_fair_batstateu: report.activity_fair_batstateu || '',
        activity_fair_other: report.activity_fair_other || '',
        activity_poor_batstateu: report.activity_poor_batstateu || '',
        activity_poor_other: report.activity_poor_other || '',
        timeliness_excellent_batstateu: report.timeliness_excellent_batstateu || '',
        timeliness_excellent_other: report.timeliness_excellent_other || '',
        timeliness_very_satisfactory_batstateu: report.timeliness_very_satisfactory_batstateu || '',
        timeliness_very_satisfactory_other: report.timeliness_very_satisfactory_other || '',
        timeliness_satisfactory_batstateu: report.timeliness_satisfactory_batstateu || '',
        timeliness_satisfactory_other: report.timeliness_satisfactory_other || '',
        timeliness_fair_batstateu: report.timeliness_fair_batstateu || '',
        timeliness_fair_other: report.timeliness_fair_other || '',
        timeliness_poor_batstateu: report.timeliness_poor_batstateu || '',
        timeliness_poor_other: report.timeliness_poor_other || ''
      });
      
      if (report.photos) {
        try {
          const photosData = typeof report.photos === 'string' ? JSON.parse(report.photos) : report.photos;
          setPhotos(photosData);
          
          if (Array.isArray(photosData) && photosData.length > 0) {
            const isRowBased = photosData[0].row_id !== undefined || photosData[0].frameCount !== undefined;
            
            if (!isRowBased) {
              const rows = [];
              for (let i = 0; i < photosData.length; i += 2) {
                const rowPhotos = photosData.slice(i, i + 2);
                const hasPhotoCaptions = rowPhotos.some(p => p.caption);
                rows.push({
                  row_id: `row_${Date.now()}_${i}`,
                  frameCount: rowPhotos.length,
                  photos: rowPhotos,
                  caption: hasPhotoCaptions ? '' : (rowPhotos[0]?.caption || ''),
                  captionType: hasPhotoCaptions ? 'photo' : 'row',
                  photoCaptions: hasPhotoCaptions ? rowPhotos.map(p => p.caption || '') : [],
                  selectedFiles: [],
                  tempFiles: []
                });
              }
              setPhotoRows(rows);
            } else {
              const rowsWithDefaults = photosData.map(row => ({
                ...row,
                selectedFiles: row.selectedFiles || [],
                tempFiles: row.tempFiles || [],
                photoCaptions: row.photoCaptions || []
              }));
              setPhotoRows(rowsWithDefaults);
            }
          } else {
            setPhotoRows([]);
          }
        } catch (e) {
          console.error('Error parsing photos:', e);
          setPhotos([]);
          setPhotoRows([]);
        }
      } else {
        setPhotoRows([]);
      }
    } catch (err) {
      console.error('Failed to load existing report', err);
      showError('Failed to load existing report');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = async (projectId) => {
    setSelectedProject(null);
    setExistingReport(null);
    setPhotos([]);
    setPhotoRows([]);
    dismiss();
    
    setParticipantData({
      participant_type: '',
      male_batstateu_participants: '',
      female_batstateu_participants: '',
      male_other_participants: '',
      female_other_participants: '',
      narrative_of_activity: '',
      activity_excellent_batstateu: '',
      activity_excellent_other: '',
      activity_very_satisfactory_batstateu: '',
      activity_very_satisfactory_other: '',
      activity_satisfactory_batstateu: '',
      activity_satisfactory_other: '',
      activity_fair_batstateu: '',
      activity_fair_other: '',
      activity_poor_batstateu: '',
      activity_poor_other: '',
      timeliness_excellent_batstateu: '',
      timeliness_excellent_other: '',
      timeliness_very_satisfactory_batstateu: '',
      timeliness_very_satisfactory_other: '',
      timeliness_satisfactory_batstateu: '',
      timeliness_satisfactory_other: '',
      timeliness_fair_batstateu: '',
      timeliness_fair_other: '',
      timeliness_poor_batstateu: '',
      timeliness_poor_other: ''
    });
    
    if (!projectId) return;
    
    const project = projects.find(p => p.project_id == projectId);
    setSelectedProject(project);
    
    try {
      const { data: fullProjectData } = await apiClient.get(`/projects/${projectId}`);
      setSelectedProject(fullProjectData);
    } catch (err) {
      console.error('Failed to load full project details', err);
    }
    
    try {
      const { data } = await apiClient.get(`/reports?project_id=${projectId}`);
      if (data && data.length > 0) {
        const report = data[0];
        setExistingReport(report);
        
        if (report.project_id == projectId) {
          setParticipantData({
            participant_type: report.participant_type || '',
            male_batstateu_participants: report.male_batstateu_participants || '',
            female_batstateu_participants: report.female_batstateu_participants || '',
            male_other_participants: report.male_other_participants || '',
            female_other_participants: report.female_other_participants || '',
            narrative_of_activity: report.narrative_of_activity || '',
            activity_excellent_batstateu: report.activity_excellent_batstateu || '',
            activity_excellent_other: report.activity_excellent_other || '',
            activity_very_satisfactory_batstateu: report.activity_very_satisfactory_batstateu || '',
            activity_very_satisfactory_other: report.activity_very_satisfactory_other || '',
            activity_satisfactory_batstateu: report.activity_satisfactory_batstateu || '',
            activity_satisfactory_other: report.activity_satisfactory_other || '',
            activity_fair_batstateu: report.activity_fair_batstateu || '',
            activity_fair_other: report.activity_fair_other || '',
            activity_poor_batstateu: report.activity_poor_batstateu || '',
            activity_poor_other: report.activity_poor_other || '',
            timeliness_excellent_batstateu: report.timeliness_excellent_batstateu || '',
            timeliness_excellent_other: report.timeliness_excellent_other || '',
            timeliness_very_satisfactory_batstateu: report.timeliness_very_satisfactory_batstateu || '',
            timeliness_very_satisfactory_other: report.timeliness_very_satisfactory_other || '',
            timeliness_satisfactory_batstateu: report.timeliness_satisfactory_batstateu || '',
            timeliness_satisfactory_other: report.timeliness_satisfactory_other || '',
            timeliness_fair_batstateu: report.timeliness_fair_batstateu || '',
            timeliness_fair_other: report.timeliness_fair_other || '',
            timeliness_poor_batstateu: report.timeliness_poor_batstateu || '',
            timeliness_poor_other: report.timeliness_poor_other || ''
          });
          
          const photosData = report.photos || [];
          setPhotos(photosData);
          
          if (Array.isArray(photosData) && photosData.length > 0) {
            const isRowBased = photosData[0].row_id !== undefined || photosData[0].frameCount !== undefined;
            if (!isRowBased) {
              const rows = [];
              for (let i = 0; i < photosData.length; i += 2) {
                const rowPhotos = photosData.slice(i, i + 2);
                const hasPhotoCaptions = rowPhotos.some(p => p.caption);
                rows.push({
                  row_id: `row_${Date.now()}_${i}`,
                  frameCount: rowPhotos.length,
                  photos: rowPhotos,
                  caption: hasPhotoCaptions ? '' : (rowPhotos[0]?.caption || ''),
                  captionType: hasPhotoCaptions ? 'photo' : 'row',
                  photoCaptions: hasPhotoCaptions ? rowPhotos.map(p => p.caption || '') : []
                });
              }
              setPhotoRows(rows);
            } else {
              setPhotoRows(photosData);
            }
          } else {
            setPhotoRows([]);
          }
        }
      } else {
        setExistingReport(null);
        setPhotos([]);
        // Initialize with 3 default photo rows when no existing report
        const defaultRows = Array.from({ length: 3 }, (_, i) => ({
          row_id: `row_${Date.now()}_${i}`,
          frameCount: 1,
          photos: [],
          caption: '',
          captionType: 'row',
          photoCaptions: [],
          selectedFiles: [],
          tempFiles: []
        }));
        setPhotoRows(defaultRows);
      }
    } catch (err) {
      console.error('Error checking existing report:', err);
      setExistingReport(null);
      setPhotos([]);
      // Initialize with 3 default photo rows on error
      const defaultRows = Array.from({ length: 3 }, (_, i) => ({
        row_id: `row_${Date.now()}_${i}`,
        frameCount: 1,
        photos: [],
        caption: '',
        captionType: 'row',
        photoCaptions: [],
        selectedFiles: [],
        tempFiles: []
      }));
      setPhotoRows(defaultRows);
    }
  };

  const handleParticipantDataChange = (field, value) => {
    setParticipantData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeletePhoto = async (photoId) => {
    if (!existingReport) return;

    try {
      await apiClient.delete(`/reports/${existingReport.report_id}/photos/${photoId}`);
      showSuccess('Photo deleted successfully!');
      setPhotos(prev => prev.filter(photo => photo.photo_id != photoId));
      await fetchExistingReport();
    } catch (err) {
      console.error('Error deleting photo:', err);
      showError('Failed to delete photo. Please try again.');
    }
  };

  const handleSubmitReport = async () => {
    if (!selectedProject) {
      showError('Please select a project first');
      return;
    }

    setSubmitting(true);
    dismiss();

    try {
      // Track if this was a new report before submission
      const isNewReport = !existingReport;
      setWasNewReport(isNewReport);

      let response;
      if (existingReport) {
        // Update existing report - use JSON payload (signature upload only works on creation)
        const payload = {
          signatory_prefs: signatoryPrefs,
          project_id: selectedProject.project_id,
          tracking_number: trackingNumber || '',
          participant_type: participantData.participant_type,
          male_batstateu_participants: parseInt(participantData.male_batstateu_participants) || 0,
          female_batstateu_participants: parseInt(participantData.female_batstateu_participants) || 0,
          male_other_participants: parseInt(participantData.male_other_participants) || 0,
          female_other_participants: parseInt(participantData.female_other_participants) || 0,
          narrative_of_activity: participantData.narrative_of_activity,
          activity_excellent_batstateu: parseInt(participantData.activity_excellent_batstateu) || 0,
          activity_excellent_other: parseInt(participantData.activity_excellent_other) || 0,
          activity_very_satisfactory_batstateu: parseInt(participantData.activity_very_satisfactory_batstateu) || 0,
          activity_very_satisfactory_other: parseInt(participantData.activity_very_satisfactory_other) || 0,
          activity_satisfactory_batstateu: parseInt(participantData.activity_satisfactory_batstateu) || 0,
          activity_satisfactory_other: parseInt(participantData.activity_satisfactory_other) || 0,
          activity_fair_batstateu: parseInt(participantData.activity_fair_batstateu) || 0,
          activity_fair_other: parseInt(participantData.activity_fair_other) || 0,
          activity_poor_batstateu: parseInt(participantData.activity_poor_batstateu) || 0,
          activity_poor_other: parseInt(participantData.activity_poor_other) || 0,
          timeliness_excellent_batstateu: parseInt(participantData.timeliness_excellent_batstateu) || 0,
          timeliness_excellent_other: parseInt(participantData.timeliness_excellent_other) || 0,
          timeliness_very_satisfactory_batstateu: parseInt(participantData.timeliness_very_satisfactory_batstateu) || 0,
          timeliness_very_satisfactory_other: parseInt(participantData.timeliness_very_satisfactory_other) || 0,
          timeliness_satisfactory_batstateu: parseInt(participantData.timeliness_satisfactory_batstateu) || 0,
          timeliness_satisfactory_other: parseInt(participantData.timeliness_satisfactory_other) || 0,
          timeliness_fair_batstateu: parseInt(participantData.timeliness_fair_batstateu) || 0,
          timeliness_fair_other: parseInt(participantData.timeliness_fair_other) || 0,
          timeliness_poor_batstateu: parseInt(participantData.timeliness_poor_batstateu) || 0,
          timeliness_poor_other: parseInt(participantData.timeliness_poor_other) || 0
        };
        response = await apiClient.put(`/reports/${existingReport.report_id}`, payload);
      } else {
        // Create new report - use FormData to support file upload (signature)
        const formData = new FormData();
        formData.append('signatory_prefs', JSON.stringify(signatoryPrefs));
        
        // Add all form fields
        formData.append('project_id', selectedProject.project_id);
        formData.append('tracking_number', trackingNumber || '');
        formData.append('participant_type', participantData.participant_type || '');
        formData.append('male_batstateu_participants', parseInt(participantData.male_batstateu_participants) || 0);
        formData.append('female_batstateu_participants', parseInt(participantData.female_batstateu_participants) || 0);
        formData.append('male_other_participants', parseInt(participantData.male_other_participants) || 0);
        formData.append('female_other_participants', parseInt(participantData.female_other_participants) || 0);
        formData.append('narrative_of_activity', participantData.narrative_of_activity || '');
        // Activity rating fields
        formData.append('activity_excellent_batstateu', parseInt(participantData.activity_excellent_batstateu) || 0);
        formData.append('activity_excellent_other', parseInt(participantData.activity_excellent_other) || 0);
        formData.append('activity_very_satisfactory_batstateu', parseInt(participantData.activity_very_satisfactory_batstateu) || 0);
        formData.append('activity_very_satisfactory_other', parseInt(participantData.activity_very_satisfactory_other) || 0);
        formData.append('activity_satisfactory_batstateu', parseInt(participantData.activity_satisfactory_batstateu) || 0);
        formData.append('activity_satisfactory_other', parseInt(participantData.activity_satisfactory_other) || 0);
        formData.append('activity_fair_batstateu', parseInt(participantData.activity_fair_batstateu) || 0);
        formData.append('activity_fair_other', parseInt(participantData.activity_fair_other) || 0);
        formData.append('activity_poor_batstateu', parseInt(participantData.activity_poor_batstateu) || 0);
        formData.append('activity_poor_other', parseInt(participantData.activity_poor_other) || 0);
        // Timeliness rating fields
        formData.append('timeliness_excellent_batstateu', parseInt(participantData.timeliness_excellent_batstateu) || 0);
        formData.append('timeliness_excellent_other', parseInt(participantData.timeliness_excellent_other) || 0);
        formData.append('timeliness_very_satisfactory_batstateu', parseInt(participantData.timeliness_very_satisfactory_batstateu) || 0);
        formData.append('timeliness_very_satisfactory_other', parseInt(participantData.timeliness_very_satisfactory_other) || 0);
        formData.append('timeliness_satisfactory_batstateu', parseInt(participantData.timeliness_satisfactory_batstateu) || 0);
        formData.append('timeliness_satisfactory_other', parseInt(participantData.timeliness_satisfactory_other) || 0);
        formData.append('timeliness_fair_batstateu', parseInt(participantData.timeliness_fair_batstateu) || 0);
        formData.append('timeliness_fair_other', parseInt(participantData.timeliness_fair_other) || 0);
        formData.append('timeliness_poor_batstateu', parseInt(participantData.timeliness_poor_batstateu) || 0);
        formData.append('timeliness_poor_other', parseInt(participantData.timeliness_poor_other) || 0);
        
        // Add signature file if provided
        if (preparedBySignature) {
          formData.append('prepared_by_signature', preparedBySignature);
        }
        
        // Set initial status to Pending
        formData.append('status', 'Pending');
        
        response = await apiClient.post('/reports', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      // Upload photos for both new and existing reports
      const reportIdToUse = existingReport ? existingReport.report_id : response.data.report_id;
      if (photoRows.length > 0 && photoRows.some(row => row.selectedFiles && row.selectedFiles.some(f => f))) {
        try {
          for (const row of photoRows) {
            const filesToUpload = row.selectedFiles?.filter(f => f) || [];
            if (filesToUpload.length === 0) continue;
            
            const formData = new FormData();
            filesToUpload.forEach((file) => {
              formData.append('photos', file);
            });
            formData.append('rowData', JSON.stringify({
              row_id: row.row_id,
              frameCount: row.frameCount,
              caption: row.caption || '',
              captionType: row.captionType || 'row',
              photoCaptions: row.photoCaptions || []
            }));
            
            await apiClient.post(`/reports/${reportIdToUse}/photos`, formData);
          }
        } catch (photoErr) {
          console.error('Error uploading photos:', photoErr);
          showError(`Report ${isNewReport ? 'created' : 'updated'} but failed to upload photos: ${photoErr.response?.data?.error || photoErr.response?.data?.message || photoErr.message}. You can upload them later by editing the report.`);
        }
      }

      setExistingReport(response.data);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error submitting report:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      
      // Build detailed error message
      let errorMessage = 'Failed to submit evaluation report.';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Use the error message from backend
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        // Add details if available (in development mode)
        if (errorData.details && process.env.NODE_ENV === 'development') {
          errorMessage += `\n\nDetails: ${JSON.stringify(errorData.details, null, 2)}`;
        } else if (errorData.details) {
          // In production, show user-friendly details
          if (errorData.details.message) {
            errorMessage += `\n\n${errorData.details.message}`;
          }
        }
        
        // Add SQL error details if available (for debugging)
        if (errorData.sqlError && process.env.NODE_ENV === 'development') {
          errorMessage += `\n\nSQL Error: ${errorData.sqlError.message || errorData.sqlError.code || 'Unknown SQL error'}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    dismiss();
    resetForm();
    onHide();
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'On-Going': return 'primary';
      case 'Pending': return 'warning';
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      default: return 'secondary';
    }
  };

  // Render Project Selection & Demographics Tab
  const renderProjectTab = () => (
    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
      {/* Tracking Number - always at the very top */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Tracking Number</Form.Label>
        <Form.Control
          type="text"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          style={{ border: '1px solid #ced4da', padding: '10px' }}
        />
        <Form.Text className="text-muted">
          This value will appear in the footer as the Tracking No. for this Evaluation Report.
        </Form.Text>
      </Form.Group>
      <hr className="my-3" style={{ borderTop: '1px solid #dee2e6' }} />
      
      <Form.Group className="mb-3">
        <Form.Label><strong>Select Project (Approved by Chancellor) for Report</strong></Form.Label>
        <Form.Select
          value={selectedProject?.project_id || ''}
          onChange={(e) => handleProjectChange(e.target.value)}
          required
          disabled={loading || isEditMode}
        >
          <option value="">
            {loading ? 'Loading projects...' : 
             isEditMode ? `Project: ${selectedProject?.title || 'Loading...'}` : 
             'Choose an approved project...'}
          </option>
          {projects.map(project => (
            <option key={project.project_id} value={project.project_id}>
              {project.title}
            </option>
          ))}
        </Form.Select>
        <Form.Text className="text-muted">
          Only projects approved by the Chancellor are available for reporting
          {projects.length > 0 && (
            <span className="ms-2">({projects.length} approved projects available)</span>
          )}
        </Form.Text>
        {!loading && projects.length === 0 && (
          <Alert variant="warning" className="mt-2">
            <i className="bi bi-exclamation-triangle me-2"></i>
            No approved projects found.
            {user && user.role === 'Extension Coordinator'
              ? ' You only see projects you coordinated that are approved by the Chancellor.'
              : ' Projects must be marked as "Approved by Chancellor" to appear in this list.'}
          </Alert>
        )}
      </Form.Group>

      {selectedProject && (
        <div className="mb-3 p-3 border rounded">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center">
              <i className="bi bi-pencil-square me-2"></i>
              <span><strong>Section to be filled out for: {selectedProject.title}</strong></span>
            </div>
          </div>
          <strong>Number of Male and Female and Type of Beneficiaries (Type such as OSY, Children, Women, etc.):</strong>
          <div className="mt-3">
            <div className="d-flex align-items-center mb-2">
              <strong>Type of participants:</strong>
              <Form.Control
                type="text"
                className="ms-2"
                style={{ maxWidth: '300px' }}
                placeholder="e.g., OSY, Children, Women, etc."
                value={participantData.participant_type}
                onChange={(e) => handleParticipantDataChange('participant_type', e.target.value)}
              />
            </div>
            <div className="table-responsive">
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '20%' }}></th>
                    <th style={{ width: '25%' }}>BatStateU Participants</th>
                    <th style={{ width: '25%' }}>Participants from other Institutions</th>
                    <th style={{ width: '15%' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="fw-bold">Male</td>
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        size="sm"
                        placeholder="0"
                        value={participantData.male_batstateu_participants}
                        onChange={(e) => handleParticipantDataChange('male_batstateu_participants', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        size="sm"
                        placeholder="0"
                        value={participantData.male_other_participants}
                        onChange={(e) => handleParticipantDataChange('male_other_participants', e.target.value)}
                      />
                    </td>
                    <td className="fw-bold text-center">
                      {(parseInt(participantData.male_batstateu_participants) || 0) + (parseInt(participantData.male_other_participants) || 0)}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Female</td>
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        size="sm"
                        placeholder="0"
                        value={participantData.female_batstateu_participants}
                        onChange={(e) => handleParticipantDataChange('female_batstateu_participants', e.target.value)}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        size="sm"
                        placeholder="0"
                        value={participantData.female_other_participants}
                        onChange={(e) => handleParticipantDataChange('female_other_participants', e.target.value)}
                      />
                    </td>
                    <td className="fw-bold text-center">
                      {(parseInt(participantData.female_batstateu_participants) || 0) + (parseInt(participantData.female_other_participants) || 0)}
                    </td>
                  </tr>
                  <tr className="table-secondary">
                    <td className="fw-bold">Grand Total</td>
                    <td className="fw-bold text-center">
                      {(parseInt(participantData.male_batstateu_participants) || 0) + (parseInt(participantData.female_batstateu_participants) || 0)}
                    </td>
                    <td className="fw-bold text-center">
                      {(parseInt(participantData.male_other_participants) || 0) + (parseInt(participantData.female_other_participants) || 0)}
                    </td>
                    <td className="fw-bold text-center">
                      {(parseInt(participantData.male_batstateu_participants) || 0) + (parseInt(participantData.female_batstateu_participants) || 0) + 
                       (parseInt(participantData.male_other_participants) || 0) + (parseInt(participantData.female_other_participants) || 0)}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
            
            <div className="mt-4">
              <strong>Narrative of the Activity:</strong>
              <Form.Control
                as="textarea"
                rows={6}
                className="mt-2"
                placeholder="Please provide a detailed narrative of the activity, including what happened, key outcomes, and any notable observations..."
                value={participantData.narrative_of_activity}
                onChange={(e) => handleParticipantDataChange('narrative_of_activity', e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>
            
            {/* Prepared By Signature Upload Section */}
            {!isEditMode && (
              <div className="mt-4 p-3 border rounded bg-light">
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-pen text-primary me-2"></i>
                  <strong className="text-primary">Prepared By Signature:</strong>
                </div>
                <p className="text-muted small mb-2">
                  <i className="bi bi-info-circle me-1"></i>
                  Upload your signature image. This will appear in the "Prepared by" section of the generated report.
                </p>
                <Form.Group className="mt-2">
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPreparedBySignature(file);
                        // Create preview URL
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setSignaturePreview(reader.result);
                        };
                        reader.readAsDataURL(file);
                      } else {
                        setPreparedBySignature(null);
                        setSignaturePreview(null);
                      }
                    }}
                  />
                  {signaturePreview && (
                    <div className="mt-3">
                      <div className="mb-2">
                        <strong>Preview:</strong>
                      </div>
                      <img 
                        src={signaturePreview} 
                        alt="Signature preview" 
                        style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid #ddd', padding: '5px', backgroundColor: 'white' }}
                        className="d-block"
                      />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setPreparedBySignature(null);
                          setSignaturePreview(null);
                          // Reset file input
                          const fileInput = document.querySelector('input[type="file"][accept="image/*"]');
                          if (fileInput) fileInput.value = '';
                        }}
                      >
                        <i className="bi bi-x-circle me-1"></i>
                        Remove Signature
                      </Button>
                    </div>
                  )}
                </Form.Group>
              </div>
            )}

            {/* Status Field - Admin Only */}
            {user && user.role === 'Admin' && isEditMode && existingReport && (
              <Form.Group className="mb-3 mt-4">
                <Form.Label><strong>Report Status:</strong></Form.Label>
                <Form.Select
                  name="status"
                  value={reportStatus || existingReport.status}
                  onChange={async (e) => {
                    const newStatus = e.target.value;
                    setReportStatus(newStatus);
                    try {
                      await apiClient.put(`/reports/${existingReport.report_id}/status`, {
                        status: newStatus
                      });
                      showSuccess('Report status updated successfully!');
                      // Refresh the report data
                      const response = await apiClient.get(`/reports/${existingReport.report_id}`);
                      setExistingReport(response.data);
                    } catch (err) {
                      showError('Failed to update status: ' + (err.response?.data?.error || err.message));
                    }
                  }}
                  style={{ border: '1px solid #888', fontSize: '1em' }}
                >
                  <option value="Pending">Pending</option>
                  <option value="Approved by VC-RDES">Approved by VC-RDES</option>
                  <option value="Rejected by VC-RDES">Rejected by VC-RDES</option>
                  <option value="Approved by Chancellor">Approved by Chancellor</option>
                  <option value="Rejected by Chancellor">Rejected by Chancellor</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Only Admin can change report status
                </Form.Text>
              </Form.Group>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Render Evaluation Results Tab
  const renderEvaluationTab = () => (
    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
      {selectedProject ? (
        <div>
          <div className="mb-4">
            <strong>Evaluation Result (if activity is training, technical advice or seminar):</strong>
            
            <div className="mt-3">
              <strong>Number of beneficiaries/participants who rated the activity as:</strong>
              <div className="table-responsive mt-2">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '30%' }}>Scale</th>
                      <th style={{ width: '25%' }}>BatStateU Participants</th>
                      <th style={{ width: '25%' }}>Participants from other Institutions</th>
                      <th style={{ width: '20%' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'excellent', label: '1.1. Excellent' },
                      { key: 'very_satisfactory', label: '1.2. Very Satisfactory' },
                      { key: 'satisfactory', label: '1.3. Satisfactory' },
                      { key: 'fair', label: '1.4. Fair' },
                      { key: 'poor', label: '1.5. Poor' }
                    ].map(({ key, label }) => (
                      <tr key={key}>
                        <td className="fw-bold">{label}</td>
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            size="sm"
                            placeholder="0"
                            value={participantData[`activity_${key}_batstateu`]}
                            onChange={(e) => handleParticipantDataChange(`activity_${key}_batstateu`, e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            size="sm"
                            placeholder="0"
                            value={participantData[`activity_${key}_other`]}
                            onChange={(e) => handleParticipantDataChange(`activity_${key}_other`, e.target.value)}
                          />
                        </td>
                        <td className="fw-bold text-center">
                          {(parseInt(participantData[`activity_${key}_batstateu`]) || 0) + (parseInt(participantData[`activity_${key}_other`]) || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
            
            <div className="mt-4">
              <strong>Number of beneficiaries/participants who rated the timeliness of the activity as:</strong>
              <div className="table-responsive mt-2">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '30%' }}>Scale</th>
                      <th style={{ width: '25%' }}>BatStateU Participants</th>
                      <th style={{ width: '25%' }}>Participants from other Institutions</th>
                      <th style={{ width: '20%' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'excellent', label: '2.1. Excellent' },
                      { key: 'very_satisfactory', label: '2.2. Very Satisfactory' },
                      { key: 'satisfactory', label: '2.3. Satisfactory' },
                      { key: 'fair', label: '2.4. Fair' },
                      { key: 'poor', label: '2.5. Poor' }
                    ].map(({ key, label }) => (
                      <tr key={key}>
                        <td className="fw-bold">{label}</td>
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            size="sm"
                            placeholder="0"
                            value={participantData[`timeliness_${key}_batstateu`]}
                            onChange={(e) => handleParticipantDataChange(`timeliness_${key}_batstateu`, e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            size="sm"
                            placeholder="0"
                            value={participantData[`timeliness_${key}_other`]}
                            onChange={(e) => handleParticipantDataChange(`timeliness_${key}_other`, e.target.value)}
                          />
                        </td>
                        <td className="fw-bold text-center">
                          {(parseInt(participantData[`timeliness_${key}_batstateu`]) || 0) + (parseInt(participantData[`timeliness_${key}_other`]) || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-info-circle" style={{ fontSize: '2rem' }}></i>
          <p className="mt-2">Please select a project first</p>
        </div>
      )}
    </div>
  );

  // Render Photos Tab with black/white styling
  const renderPhotosTab = () => (
    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
      {selectedProject ? (
        <div>
          <strong>Photos:</strong>
          
          {selectedProject && (
            <div className="mt-3 p-3 border border-secondary rounded" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="d-flex align-items-center mb-3">
                <i className="bi bi-camera text-dark me-2"></i>
                <strong className="text-dark">Upload Photos with Framing:</strong>
              </div>
              
              <div className="mb-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const newRow = {
                      row_id: `row_${Date.now()}_${Math.random()}`,
                      frameCount: 1,
                      photos: [],
                      caption: '',
                      captionType: 'row',
                      photoCaptions: [],
                      selectedFiles: [],
                      tempFiles: []
                    };
                    setPhotoRows([...photoRows, newRow]);
                  }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Add Photo Row
                </Button>
              </div>
              
              {photoRows.map((row, rowIndex) => (
                <div key={row.row_id} className="mb-4 p-3 border rounded" style={{ backgroundColor: '#ffffff' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Row {rowIndex + 1}</strong>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setPhotoRows(photoRows.filter((r, idx) => idx !== rowIndex));
                      }}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                  
                  <div className="mb-3">
                    <Form.Label><small>Number of frames:</small></Form.Label>
                    <Form.Select
                      size="sm"
                      value={row.frameCount}
                      onChange={(e) => {
                        const newFrameCount = parseInt(e.target.value);
                        const updatedRows = [...photoRows];
                        updatedRows[rowIndex] = {
                          ...row,
                          frameCount: newFrameCount,
                          photos: row.photos?.slice(0, newFrameCount) || [],
                          selectedFiles: row.selectedFiles?.slice(0, newFrameCount) || [],
                          tempFiles: row.tempFiles?.slice(0, newFrameCount) || [],
                          photoCaptions: row.photoCaptions?.slice(0, newFrameCount) || []
                        };
                        setPhotoRows(updatedRows);
                      }}
                    >
                      <option value={1}>1 Photo</option>
                      <option value={2}>2 Photos</option>
                    </Form.Select>
                  </div>
                  
                  <div className="mb-3">
                    <Form.Label><small>Caption Type:</small></Form.Label>
                    <Form.Select
                      size="sm"
                      value={row.captionType || 'row'}
                      onChange={(e) => {
                        const updatedRows = [...photoRows];
                        updatedRows[rowIndex] = { 
                          ...row, 
                          captionType: e.target.value,
                          caption: e.target.value === 'row' ? row.caption : '',
                          photoCaptions: e.target.value === 'photo' ? (row.photoCaptions || Array(row.frameCount).fill('')) : []
                        };
                        setPhotoRows(updatedRows);
                      }}
                    >
                      <option value="row">Caption per Row</option>
                      <option value="photo">Caption per Photo</option>
                    </Form.Select>
                  </div>
                  
                  <div className="mb-3">
                    <Row>
                      {Array.from({ length: row.frameCount }).map((_, photoIndex) => {
                        const hasPhoto = row.photos?.[photoIndex] || row.tempFiles?.[photoIndex];
                        const photoUrl = row.tempFiles?.[photoIndex] || (row.photos?.[photoIndex]?.url && row.photos[photoIndex].url.startsWith('http') 
                          ? row.photos[photoIndex].url 
                          : row.photos?.[photoIndex] 
                            ? `${API_HOST_BASE}/uploads/report-photos/${row.photos[photoIndex].filename}` 
                            : null);
                        
                        return (
                          <Col md={row.frameCount === 1 ? 12 : 6} key={photoIndex} className="mb-3">
                            <div 
                              className="position-relative border rounded d-flex align-items-center justify-content-center"
                              style={{ 
                                minHeight: '200px', 
                                backgroundColor: '#f8f9fa',
                                cursor: 'pointer',
                                borderStyle: 'dashed',
                                borderWidth: '2px',
                                borderColor: '#6c757d'
                              }}
                              onClick={() => {
                                if (!hasPhoto) {
                                  document.getElementById(`photo-input-${row.row_id}-${photoIndex}`)?.click();
                                }
                              }}
                            >
                              <input
                                type="file"
                                id={`photo-input-${row.row_id}-${photoIndex}`}
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const updatedRows = [...photoRows];
                                    if (!updatedRows[rowIndex].selectedFiles) {
                                      updatedRows[rowIndex].selectedFiles = [];
                                    }
                                    if (!updatedRows[rowIndex].tempFiles) {
                                      updatedRows[rowIndex].tempFiles = [];
                                    }
                                    updatedRows[rowIndex].selectedFiles[photoIndex] = file;
                                    updatedRows[rowIndex].tempFiles[photoIndex] = URL.createObjectURL(file);
                                    setPhotoRows(updatedRows);
                                  }
                                }}
                              />
                              
                              {hasPhoto ? (
                                <div className="position-relative w-100 h-100" style={{ minHeight: '200px' }}>
                                  <img
                                    src={photoUrl}
                                    alt={`Photo ${photoIndex + 1}`}
                                    className="w-100 h-100"
                                    style={{ 
                                      objectFit: 'cover',
                                      borderRadius: '4px'
                                    }}
                                    onError={(e) => {
                                      console.error('Photo failed to load:', e.target.src);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    className="position-absolute top-0 end-0 m-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const updatedRows = [...photoRows];
                                      updatedRows[rowIndex].selectedFiles = updatedRows[rowIndex].selectedFiles?.filter((_, idx) => idx !== photoIndex) || [];
                                      updatedRows[rowIndex].tempFiles = updatedRows[rowIndex].tempFiles?.filter((_, idx) => idx !== photoIndex) || [];
                                      if (updatedRows[rowIndex].photos) {
                                        updatedRows[rowIndex].photos = updatedRows[rowIndex].photos.filter((_, idx) => idx !== photoIndex);
                                      }
                                      if (updatedRows[rowIndex].photoCaptions) {
                                        updatedRows[rowIndex].photoCaptions = updatedRows[rowIndex].photoCaptions.filter((_, idx) => idx !== photoIndex);
                                      }
                                      setPhotoRows(updatedRows);
                                    }}
                                  >
                                    <i className="bi bi-x"></i>
                                  </Button>
                                </div>
                              ) : (
                                <div className="text-center text-muted">
                                  <i className="bi bi-plus-circle" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                  <div className="mt-2">
                                    <small>Click to add photo</small>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {row.captionType === 'photo' && (
                              <div className="mt-2">
                                <Form.Control
                                  type="text"
                                  placeholder={`Caption for photo ${photoIndex + 1}...`}
                                  value={row.photoCaptions?.[photoIndex] || ''}
                                  onChange={(e) => {
                                    const updatedRows = [...photoRows];
                                    if (!updatedRows[rowIndex].photoCaptions) {
                                      updatedRows[rowIndex].photoCaptions = Array(row.frameCount).fill('');
                                    }
                                    updatedRows[rowIndex].photoCaptions[photoIndex] = e.target.value;
                                    setPhotoRows(updatedRows);
                                  }}
                                  size="sm"
                                />
                              </div>
                            )}
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                  
                  {row.captionType === 'row' && (
                    <div className="mb-3">
                      <Form.Label><small>Row Caption (optional):</small></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter caption for this row..."
                        value={row.caption || ''}
                        onChange={(e) => {
                          const updatedRows = [...photoRows];
                          updatedRows[rowIndex] = { ...row, caption: e.target.value };
                          setPhotoRows(updatedRows);
                        }}
                        size="sm"
                      />
                    </div>
                  )}
                </div>
              ))}
              
              {photoRows.length === 0 && (
                <div className="text-center text-muted py-3">
                  <small>Click "Add Photo Row" to start adding photos</small>
                </div>
              )}
            </div>
          )}

        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-info-circle" style={{ fontSize: '2rem' }}></i>
          <p className="mt-2">Please select a project first</p>
        </div>
      )}
    </div>
  );

  const renderSignatoriesTab = () => (
    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
      <Alert variant="info" className="mb-3">
        <i className="bi bi-info-circle me-2"></i>
        Enable/disable which signatory sections will appear in the generated DOCX. Disabling a signatory will output no
        name/designation/date/signature for that section.
      </Alert>

      {[
        { key: 'prepared_by_enabled', id: 'er-signatory-prepared-by', label: 'Prepared by (Project Leader)' },
        { key: 'reviewed_by_enabled', id: 'er-signatory-reviewed-by', label: 'Reviewed by (VC-RDES)' },
        { key: 'accepted_by_enabled', id: 'er-signatory-accepted-by', label: 'Accepted by (Chancellor)' }
      ].map((opt) => {
        const disabled = loading || submitting;
        return (
          <div
            key={opt.key}
            role="checkbox"
            aria-checked={!!signatoryPrefs[opt.key]}
            tabIndex={0}
            className="d-flex align-items-center gap-2 mb-3"
            style={{
              cursor: disabled ? 'not-allowed' : 'pointer',
              userSelect: 'none',
              opacity: disabled ? 0.6 : 1
            }}
            onClick={() => {
              if (disabled) return;
              toggleSignatoryPref(opt.key);
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleSignatoryPref(opt.key);
              }
            }}
          >
            <input
              id={opt.id}
              type="checkbox"
              checked={!!signatoryPrefs[opt.key]}
              readOnly
              disabled={disabled}
              style={{
                pointerEvents: 'none', // ensure ONE click path (container) toggles once
                marginTop: 2
              }}
            />
            <label
              htmlFor={opt.id}
              className="mb-0"
              style={{ cursor: disabled ? 'not-allowed' : 'pointer', marginLeft: 4 }}
            >
              {opt.label}
            </label>
          </div>
        );
      })}
    </div>
  );

  if (!canViewReports) {
    return null;
  }

  return (
    <>
      <Modal 
        show={show} 
        onHide={handleClose} 
        size="xl" 
        centered 
        scrollable 
        backdrop="static" 
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clipboard-data me-2"></i>
            {isEditMode ? 'Edit Evaluation Report' : 'Create Evaluation Report'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, pointerEvents: 'auto', position: 'relative' }}>
          {notification.show && (
            <Alert variant={notification.variant} dismissible onClose={dismiss} className="m-3 mb-0">
              {notification.message}
            </Alert>
          )}
          
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading data...</div>
            </div>
          ) : (
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Nav variant="tabs" className="px-3 pt-3">
                <Nav.Item>
                  <Nav.Link eventKey="project">
                    <i className="bi bi-folder me-1"></i>
                    Project & Demographics
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="evaluation">
                    <i className="bi bi-star me-1"></i>
                    Evaluation Results
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="photos">
                    <i className="bi bi-camera me-1"></i>
                    Photos
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="signatories">
                    <i className="bi bi-pen me-1"></i>
                    Signatories
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                <Tab.Pane eventKey="project" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                  {renderProjectTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="evaluation" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                  {renderEvaluationTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="photos" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                  {renderPhotosTab()}
                </Tab.Pane>
                <Tab.Pane eventKey="signatories" style={{ position: 'relative', pointerEvents: 'auto', minWidth: '100%', width: '100%' }}>
                  {renderSignatoriesTab()}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            variant={existingReport ? "outline-primary" : "primary"}
            onClick={handleSubmitReport}
            disabled={submitting || !selectedProject}
          >
            {submitting ? (
              <>
                <i className="bi bi-hourglass-split me-2"></i>
                {existingReport ? 'Updating...' : 'Submitting...'}
              </>
            ) : (
              <>
                <i className={`bi ${existingReport ? 'bi-arrow-clockwise' : 'bi-check-circle'} me-2`}></i>
                {existingReport ? 'Update Report' : 'Submit Report'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => {
        setShowSuccessModal(false);
        if (onSuccess) onSuccess();
        handleClose();
      }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-check-circle-fill text-success me-2"></i>
            Success
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Evaluation Report {wasNewReport ? 'Submitted' : 'Updated'} Successfully</h5>
            <p className="text-muted">The Evaluation Report has been {wasNewReport ? 'saved' : 'updated'} successfully.</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => {
            setShowSuccessModal(false);
            setWasNewReport(false);
            if (onSuccess) onSuccess();
            handleClose();
          }}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EvaluationReportModal;

